import React, { useState, useEffect } from "react";
import { X, Mail, CalendarDays, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import userService from "../../../services/userServices";
import { useSiteContext } from "../../../context/SiteContext";
import Calendar from "../../calendar";
import "../../calendar/Calendar.css";
import { TIMEZONES } from "../../../utils/timezones";
import logger from '../../../utils/logger';

// Helper function to format datetime to mm/dd/yyyy hh:mm AM/PM for display
const formatDateTimeForDisplay = (datetimeString, timezone = null) => {
  if (!datetimeString) return "";

  const date = new Date(datetimeString);

  // Extract timezone identifier if provided
  let timezoneIdentifier = null;
  if (timezone) {
    if (typeof timezone === "string") {
      // Extract timezone identifier (e.g., "America/New_York" from "America/New_York (GMT-05:00)")
      timezoneIdentifier = timezone.split("(")[0].trim();
    } else {
      timezoneIdentifier = timezone;
    }
  }

  // Get date components in the specified timezone or browser's local timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timezoneIdentifier && { timeZone: timezoneIdentifier }),
  });

  const parts = formatter.formatToParts(date);
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  const year = parts.find((p) => p.type === "year").value;
  const hour = parts.find((p) => p.type === "hour").value;
  const minute = parts.find((p) => p.type === "minute").value;
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

  return `${month}/${day}/${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
};

// Helper function to parse date string from format "MM/DD/YYYY HH:MM AM/PM" to Date object
const parseDateTimeString = (dateTimeString) => {
  if (!dateTimeString || dateTimeString.trim() === "") return null;

  try {
    // Match pattern: MM/DD/YYYY HH:MM AM/PM
    const pattern =
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i;
    const match = dateTimeString.trim().match(pattern);

    if (!match) return null;

    const [, month, day, year, hour, minute, ampm] = match;
    let hours24 = parseInt(hour, 10);
    const minutes = parseInt(minute, 10);

    // Convert to 24-hour format
    if (ampm.toUpperCase() === "PM" && hours24 !== 12) {
      hours24 += 12;
    } else if (ampm.toUpperCase() === "AM" && hours24 === 12) {
      hours24 = 0;
    }

    const date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      hours24,
      minutes
    );

    // Validate the date is valid
    if (isNaN(date.getTime())) return null;

    return date;
  } catch (error) {
    return null;
  }
};

const ScheduleMeetingForm = ({
  meetingName,
  setMeetingName,
  meetingStartTime,
  setMeetingStartTime,
  meetingEndTime,
  setMeetingEndTime,
  invitesEmailInput,
  setInvitesEmailInput,
  invitesList,
  setInvitesList,
  speakersList,
  setSpeakersList,
  roomSettings,
  handleSettingChange,
  customBackgroundEnabled,
  setCustomBackgroundEnabled,
  backgroundType,
  setBackgroundType,
  backgroundColor,
  setBackgroundColor,
  gradientColor1,
  setGradientColor1,
  gradientColor2,
  setGradientColor2,
  gradientDirection,
  setGradientDirection,
  backgroundImage,
  setBackgroundImage,
  backgroundImagePreview,
  setBackgroundImagePreview,
  fileInputRef,
  exitUrlEnabled,
  setExitUrlEnabled,
  exitUrl,
  setExitUrl,
  customButtonEnabled,
  setCustomButtonEnabled,
  customButtonUrl,
  setCustomButtonUrl,
  customButtonText,
  setCustomButtonText,
  waitingRoomTextEnabled,
  setWaitingRoomTextEnabled,
  waitingRoomTitle,
  setWaitingRoomTitle,
  waitingRoomMessage,
  setWaitingRoomMessage,
  waitingRoomSubText,
  setWaitingRoomSubText,
  isValidUrl,
  handleStartMeeting,
  selectedTimezone,
  setSelectedTimezone,
}) => {
  const { user } = useSiteContext();
  // State for Co-Host autocomplete suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null); // null = not checked, true = exists, false = doesn't exist
  const [inviteEmails, setInviteEmails] = useState([]); // Track emails that need invites

  // State for Speakers
  const [speakersEmailInput, setSpeakersEmailInput] = useState("");
  const [showSpeakerSuggestions, setShowSpeakerSuggestions] = useState(false);
  const [selectedSpeakerSuggestionIndex, setSelectedSpeakerSuggestionIndex] =
    useState(-1);
  const [speakerSuggestions, setSpeakerSuggestions] = useState([]);
  const [checkingSpeakerUser, setCheckingSpeakerUser] = useState(false);
  const [speakerUserExists, setSpeakerUserExists] = useState(null);
  const [speakerInviteEmails, setSpeakerInviteEmails] = useState([]);

  const [endTimeError, setEndTimeError] = useState("");

  // State for date/time picker components
  const [startDate, setStartDate] = useState("");
  const [startHour, setStartHour] = useState("1");
  const [startMinute, setStartMinute] = useState("30");
  const [startAMPM, setStartAMPM] = useState("AM");
  const [duration, setDuration] = useState("60"); // Duration in minutes
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const timezones = TIMEZONES;

  // Calculate durationHours and durationMinutes from duration (like WebinarScheduleForm)
  const parsedDuration = parseInt(duration, 10);
  const normalizedDuration = Number.isNaN(parsedDuration) ? 0 : parsedDuration;
  const durationHours = Math.floor(normalizedDuration / 60);
  const durationMinutes = normalizedDuration % 60;

  // Time options for dropdown (15-minute intervals)
  const TIME_OPTIONS = (() => {
    const minutes = ["00", "15", "30", "45"];
    const options = [];
    for (let hour = 1; hour <= 12; hour += 1) {
      minutes.forEach((minute) => {
        options.push({
          value: `${hour}-${minute}`,
          label: `${hour}:${minute}`,
        });
      });
    }
    return options;
  })();

  // Helper function to format date as MM/DD/YYYY
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format date for display (MM/DD/YYYY)
  const formatDisplayDate = (dateValue) => {
    // Use startDateObj if available, otherwise parse from dateValue
    const dateToFormat =
      startDateObj ||
      (dateValue instanceof Date ? dateValue : parseDateFromInput(dateValue));
    if (!dateToFormat || isNaN(dateToFormat.getTime())) return "";
    return dateToFormat.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to parse date from MM/DD/YYYY
  const parseDateFromInput = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10) - 1;
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  // Initialize state from meetingStartTime (only on mount)
  const [isInitialized, setIsInitialized] = useState(false);
  const [startDateObj, setStartDateObj] = useState(null);

  useEffect(() => {
    if (!isInitialized && meetingStartTime && meetingStartTime.trim() !== "") {
      const parsedDate = parseDateTimeString(meetingStartTime);
      if (parsedDate) {
        setStartDateObj(parsedDate);
        setStartDate(formatDateForInput(parsedDate));
        let hours = parsedDate.getHours();
        const minutes = parsedDate.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        setStartHour(String(hours));
        setStartMinute(String(minutes).padStart(2, "0"));
        setStartAMPM(ampm);

        // Initialize duration from meetingEndTime if available
        if (meetingEndTime && meetingEndTime.trim() !== "") {
          const endDate = parseDateTimeString(meetingEndTime);
          if (endDate) {
            const diffMs = endDate.getTime() - parsedDate.getTime();
            const diffMinutes = Math.round(diffMs / (1000 * 60));
            if (diffMinutes > 0) {
              setDuration(diffMinutes.toString());
            }
          }
        }
      } else {
        // Initialize with current date if no meetingStartTime
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setStartDateObj(today);
        setStartDate(formatDateForInput(today));
      }
      setIsInitialized(true);
    } else if (
      !isInitialized &&
      (!meetingStartTime || meetingStartTime.trim() === "")
    ) {
      // Initialize with current date if no meetingStartTime
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setStartDateObj(today);
      setStartDate(formatDateForInput(today));
      setIsInitialized(true);
    }
  }, [meetingStartTime, meetingEndTime, isInitialized]);

  // Calculate end time and update meetingStartTime/meetingEndTime
  useEffect(() => {
    if (
      isInitialized &&
      startDateObj &&
      startHour &&
      startMinute !== "" &&
      startAMPM
    ) {
      const dateObj = new Date(startDateObj);
      if (dateObj && startHour && startMinute) {
        let hours24 = parseInt(startHour, 10);
        if (startAMPM === "PM" && hours24 !== 12) {
          hours24 += 12;
        } else if (startAMPM === "AM" && hours24 === 12) {
          hours24 = 0;
        }
        const minutes = parseInt(startMinute, 10) || 0;
        dateObj.setHours(hours24, minutes, 0, 0);

        // Extract timezone identifier from selectedTimezone
        let timezoneIdentifier = null;
        if (selectedTimezone) {
          if (typeof selectedTimezone === "string") {
            // Extract timezone identifier (e.g., "America/New_York" from "America/New_York (GMT-05:00)")
            timezoneIdentifier = selectedTimezone.split("(")[0].trim();
          } else {
            timezoneIdentifier = selectedTimezone;
          }
        }

        const formattedStart = formatDateTimeForDisplay(
          dateObj.toISOString(),
          timezoneIdentifier
        );
        if (formattedStart !== meetingStartTime) {
          setMeetingStartTime(formattedStart);
        }

        // Calculate end time from duration
        const durationMinutes = parseInt(duration || "60", 10);
        if (durationMinutes > 0) {
          const endDate = new Date(dateObj);
          endDate.setMinutes(endDate.getMinutes() + durationMinutes);

          const formattedEnd = formatDateTimeForDisplay(
            endDate.toISOString(),
            timezoneIdentifier
          );
          if (formattedEnd !== meetingEndTime) {
            setMeetingEndTime(formattedEnd);
          }
        }
      }
    }
  }, [
    startDateObj,
    startHour,
    startMinute,
    startAMPM,
    duration,
    isInitialized,
    selectedTimezone,
    meetingStartTime,
    meetingEndTime,
    setMeetingStartTime,
    setMeetingEndTime,
  ]);

  // Common email domains for suggestions
  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "mail.com",
    "protonmail.com",
  ];

  // Generate suggestions based on input
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      // If domain is partially typed, suggest completions
      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        // If @ is typed but no domain, suggest all common domains
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      // If no @ symbol, suggest adding common domains
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    // If it's a valid email format, also check if we can suggest similar emails
    if (emailRegex.test(input)) {
      // Don't add the exact input as a suggestion
      setSuggestions(newSuggestions.slice(0, 5)); // Limit to 5 suggestions
    } else {
      setSuggestions(newSuggestions.slice(0, 5));
    }

    // Show suggestions if we have any
    if (newSuggestions.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }

    // Reset selected index when suggestions change
    setSelectedSuggestionIndex(-1);
  }, [invitesEmailInput, invitesList]);

  // Generate speaker suggestions based on input
  useEffect(() => {
    const input = speakersEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSpeakerSuggestions([]);
      setShowSpeakerSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!speakersList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!speakersList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!speakersList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSpeakerSuggestions(true);
    } else {
      setShowSpeakerSuggestions(false);
    }

    setSelectedSpeakerSuggestionIndex(-1);
  }, [speakersEmailInput, speakersList]);

  // Check if user exists when valid email is entered (Co-Host)
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    // Reset state when input is cleared
    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    // Only check if it's a valid email format
    if (emailRegex.test(input)) {
      // Debounce the API call
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          // Assuming API returns { exists: true/false } or { success: true, exists: true/false }
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setUserExists(exists);
        } catch (error) {
          // If user doesn't exist, API might return 404 or similar
          // Check error response to determine if user doesn't exist
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setUserExists(false);
          } else {
            // Other errors - assume user doesn't exist for now
            setUserExists(false);
            logger("error", "Error checking user:", error);
          }
        } finally {
          setCheckingUser(false);
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
    }
  }, [invitesEmailInput]);

  // Check if speaker user exists when valid email is entered
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = speakersEmailInput.trim();

    if (input.length === 0) {
      setSpeakerUserExists(null);
      setCheckingSpeakerUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingSpeakerUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setSpeakerUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setSpeakerUserExists(false);
          } else {
            setSpeakerUserExists(false);
            logger("error", "Error checking speaker user:", error);
          }
        } finally {
          setCheckingSpeakerUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setSpeakerUserExists(null);
    }
  }, [speakersEmailInput]);

  // Handle adding co-host (existing user or invite)
  const handleAddCoHost = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        // If user doesn't exist, treat as invite
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          // On other errors, assume user doesn't exist
          isInvite = true;
          logger("error", "Error checking user:", error);
        }
      } finally {
        setCheckingUser(false);
      }
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  // Handle adding speaker (existing user or invite)
  const handleAddSpeaker = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingSpeakerUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        // If user doesn't exist, treat as invite
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          // On other errors, assume user doesn't exist
          isInvite = true;
          logger("error", "Error checking speaker user:", error);
        }
      } finally {
        setCheckingSpeakerUser(false);
      }
    }

    if (!speakersList.includes(trimmedEmail)) {
      setSpeakersList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setSpeakerInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setSpeakersEmailInput("");
      setShowSpeakerSuggestions(false);
      setSpeakerUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Speaker added");
    } else {
      toast.warning("Email already added.");
    }
  };

  return (
    <div className="rounded-xl border border-gray-600 p-4 md:p-8 primary-card-background">
      {/* Meeting Name and Your Name */}
      <div className="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Meeting Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={meetingName}
            onChange={(e) => setMeetingName(e.target.value)}
            placeholder="Enter meeting name"
            maxLength={25}
            className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
              meetingName.trim() && meetingName.trim().length < 3
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-600 focus:ring-indigo-500"
            }`}
          />
          <p className="text-gray-400 text-xs mt-1">
            {meetingName.trim() && meetingName.trim().length < 3 ? (
              <span className="text-red-400">
                Minimum 3 characters required
              </span>
            ) : (
              "25 characters max"
            )}
          </p>
        </div>
      </div>

      <div className="mt-4 flex w-full flex-wrap items-center gap-3 justify-start">
        <div className="w-full max-w-[160px] text-left text-white font-sans text-xl sm:text-2xl font-semibold">
          Date & Time
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={
              startDateObj
                ? formatDisplayDate(startDateObj)
                : startDate || "MM/DD/YYYY"
            }
            className="w-[140px] rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none"
          />
          <button
            type="button"
            onClick={() => setShowStartCalendar((prev) => !prev)}
            className="rounded-md border border-[#F4C2D7] bg-[#FFE5EE] p-2 transition-colors hover:bg-[#FAD0E3]"
          >
            <CalendarDays className="h-4 w-4 text-[#E06187]" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={`${startHour}-${startMinute}`}
            onChange={(e) => {
              const [hourStr, minuteStr] = e.target.value.split("-");
              setStartHour(hourStr);
              setStartMinute(minuteStr);
            }}
            className="rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            {TIME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={startAMPM}
            onChange={(e) => setStartAMPM(e.target.value)}
            className="rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
      {showStartCalendar && (
        <div className="mt-4 w-full flex justify-center">
          <div className="w-full max-w-[320px] rounded-[20px] border border-[#2F2E4E] bg-[#1C1B29] p-4">
            <Calendar
              value={
                startDateObj || parseDateFromInput(startDate) || new Date()
              }
              minDate={new Date()}
              onChange={(val) => {
                const date = val instanceof Date ? val : new Date(val);
                date.setHours(0, 0, 0, 0);
                setStartDateObj(date);
                setStartDate(formatDateForInput(date));
                setShowStartCalendar(false);
              }}
              calendarType="gregory"
              className="text-white"
            />
          </div>
        </div>
      )}

      <div className="mt-4 flex w-full flex-wrap items-center gap-3 justify-start">
        <div className="w-full max-w-[160px] text-left text-white font-sans text-xl sm:text-2xl font-semibold">
          Duration
        </div>
        <div className="flex items-center gap-2">
          <select
            value={durationHours}
            onChange={(e) => {
              const hours = parseInt(e.target.value, 10);
              const totalMinutes = hours * 60 + durationMinutes;
              setDuration(totalMinutes.toString());
            }}
            className="w-[80px] rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            {[...Array(13).keys()].map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">hr</span>
          <select
            value={durationMinutes}
            onChange={(e) => {
              const minutes = parseInt(e.target.value, 10);
              const totalMinutes = durationHours * 60 + minutes;
              setDuration(totalMinutes.toString());
            }}
            className="w-[80px] rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            {[0, 15, 30, 45].map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">min</span>
        </div>
      </div>

      <div className="mt-4 flex w-full flex-wrap items-center gap-3 justify-start">
        <div className="w-full max-w-[160px] text-left text-white font-sans text-xl sm:text-2xl font-semibold">
          Timezone
        </div>
        <div className="flex items-center gap-2 flex-1">
          <div className="relative w-full max-w-md">
            <select
              value={selectedTimezone}
              onChange={(e) => {
                setSelectedTimezone(e.target.value);
              }}
              className="w-full appearance-none rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Room Settings */}
      <div className="mb-6 md:mb-8 mt-8">
        {/* Co-Host */}
        {user?.plan == "Business" && (
          <div className="mb-8">
            <h3 className="text-white text-lg font-semibold mb-4">Co-Host</h3>
            <div className="relative">
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex-1 relative">
                  <input
                    type="email"
                    value={invitesEmailInput}
                    onChange={(e) => setInvitesEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      // Handle Enter key - prioritize selected suggestion
                      if (e.key === "Enter") {
                        if (
                          selectedSuggestionIndex >= 0 &&
                          suggestions[selectedSuggestionIndex]
                        ) {
                          e.preventDefault();
                          const selectedEmail =
                            suggestions[selectedSuggestionIndex];
                          handleAddCoHost(selectedEmail, null); // null means check first
                          setSelectedSuggestionIndex(-1);
                        } else if (invitesEmailInput.trim() && !checkingUser) {
                          e.preventDefault();
                          const email = invitesEmailInput.trim();
                          // Use userExists status to determine if it's an invite
                          handleAddCoHost(email, userExists === false);
                        }
                      } else if (
                        e.key === "ArrowDown" &&
                        suggestions.length > 0
                      ) {
                        e.preventDefault();
                        setShowSuggestions(true);
                        setSelectedSuggestionIndex((prev) =>
                          prev < suggestions.length - 1 ? prev + 1 : prev
                        );
                      } else if (
                        e.key === "ArrowUp" &&
                        suggestions.length > 0
                      ) {
                        e.preventDefault();
                        setShowSuggestions(true);
                        setSelectedSuggestionIndex((prev) =>
                          prev > 0 ? prev - 1 : 0
                        );
                      } else if (e.key === "Escape") {
                        setShowSuggestions(false);
                        setSelectedSuggestionIndex(-1);
                      }
                    }}
                    onFocus={() => {
                      if (invitesEmailInput.trim().length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="Add co-host email and press Enter"
                    className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {/* User Status Indicator */}
                  {invitesEmailInput.trim() && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingUser ? (
                        <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : userExists === true ? (
                        <div className="text-green-500 text-sm">✓</div>
                      ) : userExists === false ? (
                        <Mail className="w-5 h-5 text-orange-500" />
                      ) : null}
                    </div>
                  )}
                  {/* Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            handleAddCoHost(suggestion, null); // null means check first
                          }}
                          className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                            selectedSuggestionIndex === index
                              ? "bg-indigo-100"
                              : ""
                          }`}
                        >
                          <div className="text-sm text-gray-700">
                            {suggestion}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* User Exists/Invite Options */}
                  {!showSuggestions &&
                    invitesEmailInput.trim() &&
                    userExists !== null &&
                    !checkingUser && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        {userExists ? (
                          <div
                            onClick={() =>
                              handleAddCoHost(invitesEmailInput.trim(), false)
                            }
                            className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <div className="text-green-500">✓</div>
                              <div>
                                <div className="text-sm font-medium text-gray-700">
                                  {invitesEmailInput.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  User exists - Add as Co-Host
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() =>
                              handleAddCoHost(invitesEmailInput.trim(), true)
                            }
                            className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <Mail className="w-5 h-5 text-orange-500" />
                              <div>
                                <div className="text-sm font-medium text-gray-700">
                                  {invitesEmailInput.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  User not found - Send Email Invite
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
                <button
                  onClick={() => {
                    if (invitesEmailInput.trim()) {
                      const email = invitesEmailInput.trim();
                      if (userExists === false) {
                        handleAddCoHost(email, true);
                      } else {
                        handleAddCoHost(email, false);
                      }
                    }
                  }}
                  disabled={checkingUser}
                  className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingUser ? "..." : "Add"}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {invitesList.map((email, index) => {
                const isInvite = inviteEmails.includes(email);
                return (
                  <span
                    key={index}
                    className={`flex items-center px-3 py-1 rounded-full text-sm ${
                      isInvite
                        ? "bg-orange-600 text-white"
                        : "bg-gray-700 text-white"
                    }`}
                  >
                    {isInvite && <Mail className="w-3 h-3 mr-1" />}
                    {email}
                    <button
                      onClick={() => {
                        setInvitesList((prev) =>
                          prev.filter((item) => item !== email)
                        );
                        setInviteEmails((prev) =>
                          prev.filter((item) => item !== email)
                        );
                      }}
                      className="ml-2 text-gray-400 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Speakers */}
        <div className="mb-8">
          <h3 className="text-white text-lg font-semibold mb-4">Speakers</h3>
          <div className="relative">
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={speakersEmailInput}
                  onChange={(e) => setSpeakersEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (
                        selectedSpeakerSuggestionIndex >= 0 &&
                        speakerSuggestions[selectedSpeakerSuggestionIndex]
                      ) {
                        e.preventDefault();
                        const selectedEmail =
                          speakerSuggestions[selectedSpeakerSuggestionIndex];
                        handleAddSpeaker(selectedEmail, null); // null means check first
                        setSelectedSpeakerSuggestionIndex(-1);
                      } else if (
                        speakersEmailInput.trim() &&
                        !checkingSpeakerUser
                      ) {
                        e.preventDefault();
                        const email = speakersEmailInput.trim();
                        handleAddSpeaker(email, speakerUserExists === false);
                      }
                    } else if (
                      e.key === "ArrowDown" &&
                      speakerSuggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSpeakerSuggestions(true);
                      setSelectedSpeakerSuggestionIndex((prev) =>
                        prev < speakerSuggestions.length - 1 ? prev + 1 : prev
                      );
                    } else if (
                      e.key === "ArrowUp" &&
                      speakerSuggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSpeakerSuggestions(true);
                      setSelectedSpeakerSuggestionIndex((prev) =>
                        prev > 0 ? prev - 1 : 0
                      );
                    } else if (e.key === "Escape") {
                      setShowSpeakerSuggestions(false);
                      setSelectedSpeakerSuggestionIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    if (speakersEmailInput.trim().length > 0) {
                      setShowSpeakerSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSpeakerSuggestions(false), 200);
                  }}
                  placeholder="Add speaker email and press Enter"
                  className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {/* Speaker User Status Indicator */}
                {speakersEmailInput.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingSpeakerUser ? (
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : speakerUserExists === true ? (
                      <div className="text-green-500 text-sm">✓</div>
                    ) : speakerUserExists === false ? (
                      <Mail className="w-5 h-5 text-orange-500" />
                    ) : null}
                  </div>
                )}
                {/* Speaker Suggestions Dropdown */}
                {showSpeakerSuggestions && speakerSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {speakerSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          handleAddSpeaker(suggestion, null); // null means check first
                        }}
                        className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                          selectedSpeakerSuggestionIndex === index
                            ? "bg-indigo-100"
                            : ""
                        }`}
                      >
                        <div className="text-sm text-gray-700">
                          {suggestion}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Speaker User Exists/Invite Options */}
                {!showSpeakerSuggestions &&
                  speakersEmailInput.trim() &&
                  speakerUserExists !== null &&
                  !checkingSpeakerUser && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {speakerUserExists ? (
                        <div
                          onClick={() =>
                            handleAddSpeaker(speakersEmailInput.trim(), false)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-green-500">✓</div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {speakersEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User exists - Add as Speaker
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleAddSpeaker(speakersEmailInput.trim(), true)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-orange-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {speakersEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User not found - Send Email Invite
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <button
                onClick={() => {
                  if (speakersEmailInput.trim()) {
                    const email = speakersEmailInput.trim();
                    if (speakerUserExists === false) {
                      handleAddSpeaker(email, true);
                    } else {
                      handleAddSpeaker(email, false);
                    }
                  }
                }}
                disabled={checkingSpeakerUser}
                className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingSpeakerUser ? "..." : "Add"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {speakersList.map((email, index) => {
              const isInvite = speakerInviteEmails.includes(email);
              return (
                <span
                  key={index}
                  className={`flex items-center px-3 py-1 rounded-full text-sm ${
                    isInvite
                      ? "bg-orange-600 text-white"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {isInvite && <Mail className="w-3 h-3 mr-1" />}
                  {email}
                  <button
                    onClick={() => {
                      setSpeakersList((prev) =>
                        prev.filter((item) => item !== email)
                      );
                      setSpeakerInviteEmails((prev) =>
                        prev.filter((item) => item !== email)
                      );
                    }}
                    className="ml-2 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        <h3 className="text-white text-base md:text-lg font-semibold mb-4 md:mb-6">
          ROOM SETTINGS
        </h3>

        <div className="space-y-6">
          {/* Allow Chat */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Chat</h4>
              <p className="text-gray-400 text-sm">
                (Enable or disable in-meeting chat for participants.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              
              <button
                onClick={() => handleSettingChange("allowChat", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowChat
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowChat", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowChat
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Screen Sharing */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Screen Sharing</h4>
              <p className="text-gray-400 text-sm">
                (Let participants share their screen during the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
             
              <button
                onClick={() => handleSettingChange("allowScreenSharing", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowScreenSharing === false
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Host only
              </button>
              <button
                onClick={() => handleSettingChange("allowScreenSharing", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowScreenSharing === true
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Everyone
              </button>
            </div>
          </div>

          {/* Mute participants on entry */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">
                Mute participants on entry
              </h4>
              <p className="text-gray-400 text-sm">
                (Hosts can unmute participants later.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
             
              <button
                onClick={() =>
                  handleSettingChange("muteParticipantsOnEntry", false)
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.muteParticipantsOnEntry
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() =>
                  handleSettingChange("muteParticipantsOnEntry", true)
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.muteParticipantsOnEntry
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Reactions */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Reactions</h4>
              <p className="text-gray-400 text-sm">
                (Participants can use emoji reactions during the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
             
              <button
                onClick={() => handleSettingChange("allowReactions", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowReactions
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowReactions", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowReactions
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Raise Hand */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Raise Hand</h4>
              <p className="text-gray-400 text-sm">
                (Participants can raise their hand to request attention from the
                host.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
             
              <button
                onClick={() => handleSettingChange("allowRaiseHand", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowRaiseHand
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowRaiseHand", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowRaiseHand
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Participant Ranking */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Participant Ranking</h4>
              <p className="text-gray-400 text-sm">
                (Enable participants to sort or rank themselves in the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
             
              <button
                onClick={() => handleSettingChange("allowParticipantRanking", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowParticipantRanking
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowParticipantRanking", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowParticipantRanking
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Require Password */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Require Password</h4>
              <p className="text-gray-400 text-sm">
                (Participants will need to enter a password to join the
                meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
             
              <button
                onClick={() => handleSettingChange("requirePassword", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.requirePassword
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("requirePassword", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.requirePassword
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Password Input */}
          {roomSettings.requirePassword && (
            <div className="pl-8">
              <input
                type="text"
                value={roomSettings.password}
                onChange={(e) =>
                  handleSettingChange("password", e.target.value)
                }
                placeholder="Enter password"
                className="w-full max-w-xs px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* Waiting Room */}
          {user?.plan == "Business" && (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-white font-medium">Waiting Room</h4>
                <p className="text-gray-400 text-sm">
                  (Participants will be placed in a waiting room before joining
                  the meeting.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                
                <button
                  onClick={() =>
                    handleSettingChange("hostApprovalRequired", false)
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !roomSettings.hostApprovalRequired
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
                <button
                  onClick={() =>
                    handleSettingChange("hostApprovalRequired", true)
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    roomSettings.hostApprovalRequired
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
              </div>
            </div>
          )}

          {/* Exit URL */}
          {user?.plan == "Business" && (
            <div className="border-t border-gray-600 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Exit URL</h4>
                  <p className="text-gray-400 text-sm">
                    (Redirect participants to a specific URL when they leave the
                    meeting.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  
                  <button
                    onClick={() => {
                      setExitUrlEnabled(false);
                      setExitUrl("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !exitUrlEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                  <button
                    onClick={() => setExitUrlEnabled(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      exitUrlEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                </div>
              </div>

              {exitUrlEnabled && (
                <div className="pl-4">
                  <label className="block text-white text-sm font-medium mb-2">
                    Exit URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={exitUrl}
                    onChange={(e) => setExitUrl(e.target.value)}
                    placeholder="https://example.com"
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      exitUrl && !isValidUrl(exitUrl)
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-[#6A1FBF]"
                    }`}
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {exitUrl && !isValidUrl(exitUrl) ? (
                      <span className="text-red-400">
                        Please enter a valid URL
                      </span>
                    ) : (
                      "Enter a valid URL (e.g., https://example.com)"
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Custom Button */}
          {user?.plan == "Business" && (
            <div className="border-t border-gray-600 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Custom Button</h4>
                  <p className="text-gray-400 text-sm">
                    (Add a custom button with a URL link.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  
                  <button
                    onClick={() => {
                      setCustomButtonEnabled(false);
                      setCustomButtonUrl("");
                      setCustomButtonText("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !customButtonEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                  <button
                    onClick={() => setCustomButtonEnabled(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      customButtonEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                </div>
              </div>

              {customButtonEnabled && (
                <div className="pl-4 space-y-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Button Text <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customButtonText}
                      onChange={(e) => setCustomButtonText(e.target.value)}
                      placeholder="e.g., Visit our website"
                      maxLength={50}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        customButtonText.trim() &&
                        customButtonText.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {customButtonText.trim() &&
                      customButtonText.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${customButtonText.length}/50 characters`
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Button URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customButtonUrl}
                      onChange={(e) => setCustomButtonUrl(e.target.value)}
                      placeholder="https://example.com"
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        customButtonUrl && !isValidUrl(customButtonUrl)
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {customButtonUrl && !isValidUrl(customButtonUrl) ? (
                        <span className="text-red-400">
                          Please enter a valid URL
                        </span>
                      ) : (
                        "Enter a valid URL (e.g., https://example.com)"
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Waiting Room Text */}
          {user?.plan == "Business" && (
            <div className="border-t border-gray-600 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Waiting Room Text</h4>
                  <p className="text-gray-400 text-sm">
                    (Customize the waiting room message for participants.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  
                  <button
                    onClick={() => {
                      setWaitingRoomTextEnabled(false);
                      setWaitingRoomTitle("");
                      setWaitingRoomMessage("");
                      setWaitingRoomSubText("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !waitingRoomTextEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                  <button
                    onClick={() => setWaitingRoomTextEnabled(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      waitingRoomTextEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                </div>
              </div>

              {waitingRoomTextEnabled && (
                <div className="pl-4 space-y-4">
                  {/* Title Field */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={waitingRoomTitle}
                      onChange={(e) => setWaitingRoomTitle(e.target.value)}
                      placeholder="e.g., Please wait"
                      maxLength={100}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        waitingRoomTitle.trim() &&
                        waitingRoomTitle.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {waitingRoomTitle.trim() &&
                      waitingRoomTitle.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${waitingRoomTitle.length}/100 characters`
                      )}
                    </p>
                  </div>

                  {/* Message Field */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={waitingRoomMessage}
                      onChange={(e) => setWaitingRoomMessage(e.target.value)}
                      placeholder="e.g., The host will admit you shortly"
                      maxLength={200}
                      rows={3}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        waitingRoomMessage.trim() &&
                        waitingRoomMessage.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-indigo-500"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {waitingRoomMessage.trim() &&
                      waitingRoomMessage.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${waitingRoomMessage.length}/200 characters`
                      )}
                    </p>
                  </div>

                  {/* Sub Text Field */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Sub Text <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={waitingRoomSubText}
                      onChange={(e) => setWaitingRoomSubText(e.target.value)}
                      placeholder="e.g., You will be automatically admitted once approved"
                      maxLength={150}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        waitingRoomSubText.trim() &&
                        waitingRoomSubText.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {waitingRoomSubText.trim() &&
                      waitingRoomSubText.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${waitingRoomSubText.length}/150 characters`
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Custom Background */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-white font-medium">Custom Background</h4>
                <p className="text-gray-400 text-sm">
                  (Add a custom background to your meeting room.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
               
                <button
                  onClick={() => {
                    setCustomBackgroundEnabled(false);
                    // Clear background image when turning off
                    setBackgroundImage(null);
                    setBackgroundImagePreview(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !customBackgroundEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
                <button
                  onClick={() => setCustomBackgroundEnabled(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    customBackgroundEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
              </div>
            </div>

            {customBackgroundEnabled && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setBackgroundType("solid")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "solid"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Solid Color
                  </button>
                  <button
                    onClick={() => setBackgroundType("gradient")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "gradient"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Gradient
                  </button>
                  <button
                    onClick={() => setBackgroundType("image")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "image"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Image
                  </button>
                </div>

                {/* Solid Color Picker */}
                {backgroundType === "solid" && (
                  <div className="pl-4">
                    <label className="block text-white text-sm font-medium mb-2">
                      Background Color
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        placeholder="#1a1a2e"
                        className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <div
                        className="w-20 h-10 rounded border border-gray-600"
                        style={{ backgroundColor }}
                      />
                    </div>
                  </div>
                )}

                {/* Gradient Color Picker */}
                {backgroundType === "gradient" && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        First Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={gradientColor1}
                          onChange={(e) => setGradientColor1(e.target.value)}
                          className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={gradientColor1}
                          onChange={(e) => setGradientColor1(e.target.value)}
                          placeholder="#1a1a2e"
                          className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Second Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={gradientColor2}
                          onChange={(e) => setGradientColor2(e.target.value)}
                          className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={gradientColor2}
                          onChange={(e) => setGradientColor2(e.target.value)}
                          placeholder="#615fff"
                          className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Gradient Direction
                      </label>
                      <select
                        value={gradientDirection}
                        onChange={(e) => setGradientDirection(e.target.value)}
                        className="w-full px-4 py-2 bg-white border border-gray-600 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="to right">Left to Right</option>
                        <option value="to left">Right to Left</option>
                        <option value="to bottom">Top to Bottom</option>
                        <option value="to top">Bottom to Top</option>
                        <option value="to bottom right">
                          Top Left to Bottom Right
                        </option>
                        <option value="to bottom left">
                          Top Right to Bottom Left
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Preview
                      </label>
                      <div
                        className="w-full h-20 rounded border border-gray-600"
                        style={{
                          background: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                {backgroundType === "image" && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Background Image
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Validate file size (5MB max)
                            const maxSize = 5 * 1024 * 1024;
                            if (file.size > maxSize) {
                              toast.error("Image size should not exceed 5MB");
                              e.target.value = "";
                              return;
                            }
                            setBackgroundImage(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setBackgroundImagePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-gray-400
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-[#6A1FBF] file:text-white
                        hover:file:bg-indigo-700
                        file:cursor-pointer cursor-pointer"
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        Maximum file size: 5MB
                      </p>
                      {backgroundImage && (
                        <p className="text-green-400 text-xs mt-1">
                          Selected: {backgroundImage.name} (
                          {(backgroundImage.size / 1024).toFixed(2)} KB)
                        </p>
                      )}
                    </div>

                    {backgroundImagePreview && (
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Preview
                        </label>
                        <div className="relative w-full h-64 rounded-lg overflow-hidden border border-gray-600 bg-gray-800">
                          <img
                            src={backgroundImagePreview}
                            alt="Background preview"
                            className="w-full h-full object-contain"
                          />
                          <button
                            onClick={() => {
                              setBackgroundImage(null);
                              setBackgroundImagePreview(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
                          >
                            <X size={16} />
                            <span className="text-sm">Remove</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save as Preset */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-white font-medium">Save as Preset</h4>
                <p className="text-gray-400 text-sm">
                  (Save these settings as a preset for future meetings.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
               
                <button
                  onClick={() => {
                    handleSettingChange("saveAsPreset", false);
                    handleSettingChange("presetName", "");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !roomSettings.saveAsPreset
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
                <button
                  onClick={() => handleSettingChange("saveAsPreset", true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    roomSettings.saveAsPreset
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
              </div>
            </div>
          </div>

          {/* Preset Name Input */}
          {roomSettings.saveAsPreset && (
            <div className="pl-8">
              <input
                type="text"
                value={roomSettings.presetName}
                onChange={(e) =>
                  handleSettingChange("presetName", e.target.value)
                }
                placeholder="Enter preset name"
                className="w-full max-w-xs px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Start Meeting Button */}
      <div className="flex flex-col md:flex-row justify-end gap-2 md:gap-0">
        <button
          onClick={() => handleStartMeeting(true)}
          className="bg-[#6A1FBF] text-white font-bold py-2.5 md:py-3 px-6 md:px-8 rounded-lg hover:bg-[#6A1FBF] transition-colors md:mr-2 text-sm md:text-base"
        >
          Meet Now
        </button>
        <button
          onClick={() => handleStartMeeting(false)}
          className="bg-[#6A1FBF] text-white font-bold py-2.5 md:py-3 px-6 md:px-8 rounded-lg hover:bg-[#6A1FBF] transition-colors text-sm md:text-base"
        >
          Schedule Meeting
        </button>
      </div>
    </div>
  );
};

export default ScheduleMeetingForm;
